import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Heading, Skeleton, Stepper, Text, type StepperStep } from "@lendcord/concord";
import { ApplicationDetailView } from "@/features/onboarding/ApplicationDetailView";
import { useGetApplicationQuery } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { VerificationStep } from "./VerificationStep";
import { LoanTermsStep } from "./LoanTermsStep";
import { CrifStep } from "./CrifStep";
import { ApprovalStep } from "./ApprovalStep";
import { ApplicationActions } from "./ApplicationActions";
import { ApplicantProfileSection } from "./ApplicantProfileSection";
import type { ApplicationState } from "@/features/onboarding/onboarding.types";
import { getJourneySteps, getStateOrder, requiresLoanTermsAndCrif } from "./applicationJourney";

interface ApplicationReviewFlowProps {
  applicationId: string;
  onBack: () => void;
}

const TERMINAL_STATUSES = new Set(["APPROVED", "REJECTED", "DELETED"]);

// The application's own `status`/`state` (from GET .../applications/{id}) is
// the single source of truth for routing here — no local "which step" state.
// `state` only moves when a mutation actually completes (see lendcord-core's
// docs/flows/onboarding-review-flow.md), so re-rendering off the query's
// fresh value on every render is exactly the intended navigation model.
export function ApplicationReviewFlow({ applicationId, onBack }: ApplicationReviewFlowProps) {
  const intl = useIntl();
  const { data: application, isLoading, error } = useGetApplicationQuery(applicationId);

  // Lets a reviewer click back onto an already-completed step to view/redo
  // its form, without losing track of the application's real live state.
  // Reset whenever the backend's own state actually moves (e.g. a redone
  // step's mutation succeeds and regresses it per lendcord-core's "going
  // back" doc) so the override never goes stale.
  const [viewOverride, setViewOverride] = useState<ApplicationState | null>(null);
  useEffect(() => {
    setViewOverride(null);
  }, [application?.state]);

  const backLink = (
    <div className="mb-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={intl.formatMessage({ id: "lender.onboarding.backToApplications" })}
        className="text-sm font-medium text-accent-600 hover:underline"
      >
        ‹ <FormattedMessage id="lender.onboarding.backToApplications" />
      </button>
    </div>
  );

  if (error) {
    return (
      <div>
        {backLink}
        <AlertBanner status="error">
          {isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.detailLoadError" })}
        </AlertBanner>
      </div>
    );
  }

  if (isLoading || !application) {
    return (
      <div>
        {backLink}
        <Skeleton variant="text" className="h-4 w-32" />
        <Skeleton variant="text" className="mt-1 h-9 w-64" />
        <Skeleton variant="rect" className="mt-4 h-1.5 w-full" />
      </div>
    );
  }

  // The routing table's first row: terminal statuses get the existing
  // read-only detail view untouched, no stepper, no edit actions.
  if (TERMINAL_STATUSES.has(application.status)) {
    return <ApplicationDetailView applicationId={applicationId} onBack={onBack} />;
  }

  const journeySteps = getJourneySteps(application.applicationType);
  const stateOrder = getStateOrder(application.applicationType);
  const currentIndex = stateOrder.indexOf(application.state);
  const activeState = viewOverride ?? application.state;
  const isViewingEarlierStep = viewOverride !== null && viewOverride !== application.state;

  // Step statuses always reflect the real, live state — only the form
  // rendered below the Stepper follows activeState. A permanent "complete"
  // NEW_APPLICATION segment is prepended so this reads as a continuation of
  // the same Stepper seen on the submission success screen (see
  // OnboardingPage's "form" view). journeySteps/stateOrder are filtered to
  // this application's own applicationType — see applicationJourney.ts.
  const steps: StepperStep[] = journeySteps.map((step, journeyIndex) => {
    if (step.id === "NEW_APPLICATION") {
      return { id: step.id, label: intl.formatMessage({ id: step.labelId }), status: "complete" };
    }
    const stateIndex = journeyIndex - 1;
    const status: StepperStep["status"] = stateIndex < currentIndex ? "complete" : stateIndex === currentIndex ? "current" : "upcoming";
    return { id: step.id, label: intl.formatMessage({ id: step.labelId }), status };
  });

  function handleStepClick(step: StepperStep) {
    if (step.id === "NEW_APPLICATION") return;
    setViewOverride(step.id as ApplicationState);
  }

  return (
    <div>
      {backLink}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <Stepper steps={steps} className="min-w-0 flex-1" onStepClick={handleStepClick} />
        <ApplicationActions applicationId={applicationId} isEditAllowed={application.isEditAllowed} />
      </div>

      {isViewingEarlierStep && (
        <AlertBanner status="info" className="mt-4">
          <FormattedMessage id="lender.onboarding.review.viewingEarlierStepHint" />
        </AlertBanner>
      )}

      {activeState === "READY_FOR_REVIEW" && (
        <>
          <ApplicantProfileSection applicationId={applicationId} />
          <VerificationStep applicationId={applicationId} />
        </>
      )}
      {activeState === "VERIFIED" && requiresLoanTermsAndCrif(application.applicationType) && (
        <>
          <ApplicantProfileSection applicationId={applicationId} />
          <LoanTermsStep applicationId={applicationId} />
        </>
      )}
      {activeState === "VERIFIED" && !requiresLoanTermsAndCrif(application.applicationType) && (
        <>
          <ApplicantProfileSection applicationId={applicationId} />
          <ApprovalStep applicationId={applicationId} isEditAllowed={application.isEditAllowed} />
        </>
      )}
      {(activeState === "LOAN_DETAILS_VERIFICATION" || activeState === "CIBIL_VERIFICATION") && (
        <CrifStep applicationId={applicationId} isEditAllowed={application.isEditAllowed} />
      )}
      {activeState !== "READY_FOR_REVIEW" &&
        activeState !== "VERIFIED" &&
        activeState !== "LOAN_DETAILS_VERIFICATION" &&
        activeState !== "CIBIL_VERIFICATION" && (
          <div className="mt-6 rounded-md border border-border bg-surface-raised p-6 text-center">
            <Heading level={4} className="text-text-muted">
              <FormattedMessage id="lender.onboarding.review.nextStepNotBuiltHeading" />
            </Heading>
            <Text as="p" size="sm" variant="muted" className="mt-1">
              <FormattedMessage id="lender.onboarding.review.nextStepNotBuiltDescription" />
            </Text>
          </div>
        )}
    </div>
  );
}
